import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import screenshotService from '../lib/screenshot.service';
import { useCaptureFlags } from './use-features';

import type { ScreenshotFilter, ScreenshotListResponse } from '../types';

// Query keys
export const screenshotKeys = {
  all: ['screenshots'] as const,
  lists: () => [...screenshotKeys.all, 'list'] as const,
  list: (deviceId: string, filter?: ScreenshotFilter) => [...screenshotKeys.lists(), deviceId, filter] as const,
  detail: (deviceId: string, screenshotId: string) => [...screenshotKeys.all, 'detail', deviceId, screenshotId] as const,
};

// List screenshots for a device. Auto-refetches every 5s ONLY in async
// capture mode and only while the current page still has a row likely
// waiting on the device upload. That's how the thumb gets a fresh
// signed URL once the object lands in GCS without a page reload.
//
// A row counts as "still pending" when:
//   - it was captured less than 1 minute ago (upload window), AND
//   - file_size == 0 (unknown — not yet verified, not failed)
//
// file_size semantics (set by the backend):
//   -1  → device declined / timeout → "Image unavailable", no polling
//    0  → status unknown (ACK not yet processed OR GCS HEAD failed)
//   > 0 → verified uploaded; polling adds nothing since the row is final
//
// In SYNC mode (wait_for_ack=true) the HTTP capture call only returns
// after the device has confirmed upload AND the backend has verified
// the GCS object, so the row and image are both live by the time the
// mutation's onSuccess triggers a single refetch. No interval polling
// is needed there.
export function useScreenshots(deviceId: string, filter?: ScreenshotFilter) {
  const { wait_for_ack: waitForAck } = useCaptureFlags();
  const PENDING_THRESHOLD_MS = 60 * 1000;
  const PENDING_POLL_MS = 5_000;
  return useQuery({
    queryKey: screenshotKeys.list(deviceId, filter),
    queryFn: () => screenshotService.list(deviceId, filter),
    enabled: !!deviceId,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    refetchInterval: (q) => {
      if (waitForAck) return false;
      const data = q.state.data as ScreenshotListResponse | undefined;
      if (!data?.screenshots?.length) return false;
      const cutoff = Date.now() - PENDING_THRESHOLD_MS;
      const hasPending = data.screenshots.some((s) => {
        // Only file_size == 0 is "still unknown". -1 (failed) and
        // > 0 (verified) are both terminal states.
        if (s.file_size !== 0) return false;
        const t = new Date(s.captured_at).getTime();
        return !Number.isNaN(t) && t > cutoff;
      });
      return hasPending ? PENDING_POLL_MS : false;
    },
  });
}

// Capture screenshot mutation. Behavior depends on the server's
// `capture.wait_for_ack` flag:
//   - true  (sync):  HTTP blocks until device ACK. `isPending` stays
//                    truthy for the whole duration; errors surface via
//                    onError with a specific status code.
//   - false (async): HTTP returns immediately with screenshot_id. The
//                    new row lands on the next list refetch.
//
// We deliberately do NOT spawn a separate post-capture poll loop here.
// `useScreenshots` already polls every 5s while any row is < 2 min old,
// so the new row (which *is* recent by definition) will keep the list
// refetching on its own. Doing both caused ~2x /screenshots calls per
// tick — the two schedules overlapped inside the 5s window and
// React Query couldn't dedupe requests that weren't concurrently
// in-flight.
export function useCaptureScreenshot(deviceId: string) {
  const queryClient = useQueryClient();
  const { wait_for_ack: waitForAck } = useCaptureFlags();

  return useMutation({
    mutationFn: () => screenshotService.capture(deviceId, waitForAck),
    onSuccess: () => {
      // Single immediate refetch so the "Loading…" tile shows up right
      // away. After that, the list's own refetchInterval takes over.
      // Counts query is also refetched so the chip badges increment
      // immediately (in_progress / total go up by one).
      queryClient.refetchQueries({
        queryKey: screenshotKeys.lists(),
        type: 'active',
      });
      queryClient.refetchQueries({
        queryKey: [...screenshotKeys.all, 'counts'],
        type: 'active',
      });
    },
  });
}

// Delete screenshot mutation — removes the thumbnail from the UI
// immediately (optimistic) and rolls back if the server errors.
export function useDeleteScreenshot(deviceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (screenshotId: string) => screenshotService.delete(deviceId, screenshotId),
    onMutate: async (screenshotId: string) => {
      const listKey = screenshotKeys.lists();
      await queryClient.cancelQueries({ queryKey: listKey });
      // Snapshot every matching list-cache entry (there can be multiple
      // filter variants cached: different offset/limit) so we can roll
      // back in onError.
      const snapshots = queryClient.getQueriesData<ScreenshotListResponse>({ queryKey: listKey });
      queryClient.setQueriesData<ScreenshotListResponse>({ queryKey: listKey }, (prev) => {
        if (!prev?.screenshots) return prev;
        const filtered = prev.screenshots.filter((s) => s.id !== screenshotId);
        if (filtered.length === prev.screenshots.length) return prev;
        return {
          ...prev,
          screenshots: filtered,
          total: Math.max(0, prev.total - 1),
        };
      });
      return { snapshots };
    },
    onError: (_err, _screenshotId, context) => {
      if (!context?.snapshots) return;
      for (const [key, data] of context.snapshots) {
        queryClient.setQueryData(key, data);
      }
    },
    // Always reconcile with server state once the request is done. Scope
    // to the active list query so we don't spawn extra XHRs for stale
    // page-size variants that may still live in the cache. Also refresh
    // the global per-status counts powering the filter-chip badges.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: screenshotKeys.lists(),
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: [...screenshotKeys.all, 'counts'],
        refetchType: 'active',
      });
    },
  });
}

// Get single screenshot with download URL
export function useScreenshot(deviceId: string, screenshotId: string) {
  return useQuery({
    queryKey: screenshotKeys.detail(deviceId, screenshotId),
    queryFn: () => screenshotService.get(deviceId, screenshotId),
    enabled: !!deviceId && !!screenshotId,
  });
}

// Per-status global counts for the filter-chip badges. Invalidated
// alongside the list (via WS-driven invalidation + delete optimistic
// rollback) so the counts stay in sync.
export function useScreenshotCounts(deviceId: string) {
  return useQuery({
    queryKey: [...screenshotKeys.all, 'counts', deviceId],
    queryFn: () => screenshotService.getCounts(deviceId),
    enabled: !!deviceId,
    staleTime: 10_000,
  });
}

// Fetch a device_commands row by id. Powers the lifecycle timeline in
// the screen-capture detail panel. Polls every 3s while the command is
// non-terminal so the timeline animates in even if the user opens the
// drawer before the device ACKs.
export function useDeviceCommand(commandId: string | null | undefined) {
  return useQuery({
    queryKey: ['device-command', commandId],
    queryFn: () => screenshotService.getCommand(commandId!),
    enabled: !!commandId,
    refetchInterval: (q) => {
      const cmd = q.state.data;
      if (!cmd) return 3_000;
      const terminal = ['succeeded', 'failed', 'timeout', 'cancelled'].includes(cmd.state);
      return terminal ? false : 3_000;
    },
  });
}
